import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  message?: string;
}

/** Top-level error boundary for production resilience (failed chunk / render). */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(err: Error): State {
    const isChunk =
      err.name === 'ChunkLoadError' || /Loading chunk .* failed/i.test(err.message);
    if (isChunk && !sessionStorage.getItem('nls-chunk-reload')) {
      sessionStorage.setItem('nls-chunk-reload', '1');
      window.location.reload();
    }
    return { hasError: true, message: err.message };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 px-4 text-center">
          <h1 className="text-xl font-semibold text-slate-800 mb-2">Something went wrong</h1>
          <p className="text-slate-600 max-w-md mb-6">
            Please refresh the page. If the problem continues, try again later.
          </p>
          <button
            type="button"
            className="px-4 py-2 rounded-lg bg-blue-600 text-white font-medium"
            onClick={() => window.location.reload()}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
